import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../../services/api';

const UserForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    // Form state
    const [formData, setFormData] = useState({
                                                 name: '',
                                                 email: '',
                                                 username: '',
                                                 phone: '',
                                                 roles: [],
                                                 isActive: true,
                                                 notification_preferences: {
                                                     email: true,
                                                     sms: false
                                                 },
                                                 password: '',
                                                 confirmPassword: ''
                                             });

    // UI state
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [availableRoles, setAvailableRoles] = useState([]);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';


    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Load available roles
                const roles = await userService.getRoles();
                console.log(roles);
                setAvailableRoles(roles.data);

                // If editing, load user data
                if (isEditing) {
                    const userData = await userService.getUser(id);

                    // Check if the roles data is directly on userData or nested under userData.data
                    // This depends on your API response structure
                    const userDataObj = userData.data || userData;
                    const userRoles = userDataObj.roles || [];

                    setFormData({
                                    ...userDataObj,
                                    password: '',
                                    confirmPassword: '',
                                    roles: userRoles.map(role => role.id)
                                });
                }
            } catch (error) {
                console.error("Failed to load data:", error);
                // Show error notification
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id, isEditing]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === 'checkbox') {
            if (name.startsWith('notification_')) {
                const prefName = name.replace('notification_', '');
                setFormData(prev => ({
                    ...prev,
                    notification_preferences: {
                        ...prev.notification_preferences,
                        [prefName]: checked
                    }
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    [name]: checked
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error when field is changed
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    const handleRoleChange = (roleId) => {
        setFormData(prev => {
            const isSelected = prev.roles.includes(roleId);
            let updatedRoles;

            if (isSelected) {
                updatedRoles = prev.roles.filter(id => id !== roleId);
            } else {
                updatedRoles = [...prev.roles, roleId];
            }

            return {
                ...prev,
                roles: updatedRoles
            };
        });

        // Clear roles error if any
        if (errors.roles) {
            setErrors(prev => ({
                ...prev,
                roles: undefined
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Required fields
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';

        // Email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.email && !emailRegex.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        // Password requirements for new users
        if (!isEditing) {
            if (!formData.password) {
                newErrors.password = 'Password is required';
            } else if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            }

            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        } else if (formData.password) {
            // If editing and password field is filled
            if (formData.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters';
            }

            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = 'Passwords do not match';
            }
        }

        // At least one role is required
        if (formData.roles.length === 0) {
            newErrors.roles = 'At least one role is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitLoading(true);

        try {
            // Prepare data for submission
            const userData = { ...formData };

            // Remove confirmPassword field
            delete userData.confirmPassword;

            // If editing and password is empty, remove it
            if (isEditing && !userData.password) {
                delete userData.password;
            }

            // Submit form
            if (isEditing) {
                await userService.updateUser(id, userData);
                alert("User updated successfully"); // Replace with proper toast notification
            } else {
                await userService.createUser(userData);
                alert("User created successfully"); // Replace with proper toast notification
            }

            // Navigate back to users list
            navigate('/users');
        } catch (error) {
            console.error("Error saving user:", error);
            alert(error.message); // Replace with proper toast notification
        } finally {
            setSubmitLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {isEditing ? 'Edit User' : 'Create New User'}
                    </h2>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Basic Information */}
                            <div className="space-y-6">
                                <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>

                                <div className="space-y-2">
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        className={`w-full px-3 py-2 border ${errors.name ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        value={formData.name}
                                        onChange={handleInputChange}
                                    />
                                    {errors.name && (
                                        <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className={`w-full px-3 py-2 border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                    />
                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                                        Username
                                    </label>
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.username}
                                        onChange={handleInputChange}
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Optional. If not provided, email will be used for login.
                                    </p>
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        name="isActive"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={formData.is_active}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                                        Active Account
                                    </label>
                                </div>
                            </div>

                            {/* Password & Roles */}
                            <div className="space-y-6">
                                <h3 className="text-lg font-medium text-gray-900">Security & Permissions</h3>

                                <div className="space-y-2">
                                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                        Password {!isEditing && '*'}
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        className={`w-full px-3 py-2 border ${errors.password ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        value={formData.password}
                                        onChange={handleInputChange}
                                    />
                                    {isEditing && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            Leave blank to keep current password.
                                        </p>
                                    )}
                                    {errors.password && (
                                        <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                        Confirm Password {!isEditing && '*'}
                                    </label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        className={`w-full px-3 py-2 border ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                    />
                                    {errors.confirmPassword && (
                                        <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Roles *
                                    </label>
                                    <div className="mt-2 space-y-2">
                                        {availableRoles.map(role => (
                                            <div key={role.id} className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id={`role_${role.id}`}
                                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                    checked={formData.roles.includes(role.id)}
                                                    onChange={() => handleRoleChange(role.id)}
                                                />
                                                <label htmlFor={`role_${role.id}`} className="ml-2 block text-sm text-gray-700">
                                                    {role.name}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    {errors.roles && (
                                        <p className="mt-1 text-sm text-red-600">{errors.roles}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Notification Preferences */}
                        <div className="pt-6 border-t border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>

                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="notification_email"
                                        name="notification_email"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={formData.notification_preferences.email}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="notification_email" className="ml-2 block text-sm text-gray-700">
                                        Email Notifications
                                    </label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="notification_sms"
                                        name="notification_sms"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        checked={formData.notification_preferences.sms}
                                        onChange={handleInputChange}
                                    />
                                    <label htmlFor="notification_sms" className="ml-2 block text-sm text-gray-700">
                                        SMS Notifications
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
                        <button
                            type="button"
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={() => navigate('/users')}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${submitLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            disabled={submitLoading}
                        >
                            {submitLoading ? 'Saving...' : isEditing ? 'Update User' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserForm;
